/**
 * 权限控制工具函数 — 前端页面显隐与访问判定。
 *
 * 双模型说明（勿与后端写接口鉴权混为一谈）：
 * - **写操作 / 管理 API**：后端以 JWT `role=admin` 为准（requireAdmin）。
 * - **页面显隐**：由权限点名称 + `restrict_access` 决定（shouldShowPage / canAccessPage）。
 * - **进入管理后台**：需「管理后台访问」权限点，或 JWT 角色为 admin。
 *
 * 权限预览 Tab 可查看某用户在此模型下的实际 pageAccess，与 role 是否 admin 并列展示。
 */

// 检查是否为访客用户（未登录）
export const isGuestUser = () => {
  const token = localStorage.getItem('token');
  return !token;
};

// 获取用户权限列表
export const getUserPermissions = () => {
  try {
    // 如果是访客用户，返回访客权限
    if (isGuestUser()) {
      return getGuestPermissions();
    }
    return JSON.parse(localStorage.getItem('permissions') || '[]');
  } catch (error) {
    console.error('获取用户权限失败:', error);
    return [];
  }
};

function stripCommandPerms(list) {
  return (Array.isArray(list) ? list : []).filter(
    (name) => name !== '指挥系统页面' && !String(name).startsWith('指挥系统-')
  );
}

// 获取访客权限列表 - 从后端获取或使用默认值
export const getGuestPermissions = () => {
  try {
    // 尝试从localStorage获取访客权限配置
    const guestPermissions = localStorage.getItem('guestPermissions');
    if (guestPermissions) {
      const list = stripCommandPerms(JSON.parse(guestPermissions));
      if (list.length > 0) return list;
    }
  } catch (error) {
    console.error('获取访客权限配置失败:', error);
  }

  // 访客组未下发时的安全回落（不含指挥系统，未登录不可见）
  return ['工具下载', '坐标转换'];
};

// 设置访客权限（由后端API调用）
export const setGuestPermissions = (permissions) => {
  try {
    // 未登录绝不缓存指挥系统权限，避免导航误显示
    localStorage.setItem('guestPermissions', JSON.stringify(stripCommandPerms(permissions)));
  } catch (error) {
    console.error('设置访客权限失败:', error);
  }
};

// 获取权限配置信息（包含访问控制设置）
export const getPermissionConfig = () => {
  try {
    return JSON.parse(localStorage.getItem('permissionConfig') || '[]');
  } catch (error) {
    console.error('获取权限配置失败:', error);
    return [];
  }
};

// 检查是否为管理员（JWT role / localStorage）
export const isAdminUser = () => {
  try {
    if (localStorage.getItem('role') === 'admin') return true;
    const token = localStorage.getItem('token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.role === 'admin';
  } catch (error) {
    return false;
  }
};

// 从 JWT 解析当前用户 id
export const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload?.id ?? null;
  } catch (error) {
    return null;
  }
};

/**
 * 敏感后台权限：无论权限点管理里是否勾成「所有用户可见」，
 * 都必须显式拥有该权限点（系统管理员 role=admin 仍走 hasPermission 全放行）。
 * 避免「管理员组」误开分组授权/改标题栏等能力。
 */
/** 指挥系统操作级权限（需配合「指挥系统页面」使用） */
export const COMMAND_FEATURE_PERMS = {
  FETCH_TRACK: '指挥系统-获取轨迹',
  VIEW_TRACK: '指挥系统-查看轨迹',
  DOWNLOAD_TRACK: '指挥系统-下载轨迹',
  EXPORT_TRACK: '指挥系统-导出轨迹',
  PLAYBACK: '指挥系统-轨迹回放',
  REALTIME_DRAW: '指挥系统-实时绘制',
  DRAW_BOARD: '指挥系统-地图画板',
  BOARD_PUSH: '指挥系统-画板推送',
  BASEMAP: '指挥系统-底图切换',
  MEMBER_COMMAND: '指挥系统-队员指令',
  MISSION_BOARD_EDIT: '指挥系统-任务看板编辑',
  VIEW_ALL_MEMBERS: '指挥系统-查看全部队员',
  VIEW_SCOPED_MEMBERS: '指挥系统-查看授权组织队员',
  MEMBER_NOTES: '指挥系统-队员备注',
  ORTHO_MANAGE: '航拍建模',
  ORTHO_APPROVE: '航拍建模审批',
  ORTHO_VIEW: '航拍图层查看',
  MAP_LAYER_MANAGE: '地图图层管理',
  MAP_LAYER_VIEW: '地图图层查看',
  PANORAMA_MANAGE: '全景影像管理',
  PANORAMA_VIEW: '全景影像查看'
};

export const ALWAYS_RESTRICTED_PERMISSIONS = new Set([
  '管理后台访问',
  '分组管理',
  '权限管理',
  '用户授权',
  '用户管理',
  '会员信息管理',
  '工具管理',
  '公告管理',
  '坐标转换历史管理',
  '成品管理',
  '系统设置',
  '首页设置',
  '标题栏设置',
  '导航栏设置',
  '指挥系统密码',
  '指挥系统设置',
  '前线队员设置',
  '轨迹数据管理',
  '地图设置',
  '底图源设置',
  '单兵 App 设置',
  '数据库结构',
  '管理员密码管理',
  '图标库管理',
  '分组显示',
  ...Object.values(COMMAND_FEATURE_PERMS)
]);

/**
 * 指挥系统子功能：须已能进指挥页，且拥有对应操作权限。
 * 系统管理员（role=admin）视为全部拥有。
 */
export function hasCommandFeature(featurePermName) {
  if (isAdminUser()) return true;
  if (!hasPermission('指挥系统页面')) return false;
  // 库中尚未创建该子权限点时：有「指挥系统页面」即放行（避免未跑 seed 时功能全灭）
  const permissionConfig = getPermissionConfig();
  const existsInDb = Array.isArray(permissionConfig)
    && permissionConfig.some((p) => p?.name === featurePermName);
  if (!existsInDb) return true;
  return hasPermission(featurePermName);
}

/**
 * 任务看板写操作：与后端 canEditMissionBoard 一致。
 * 「指挥系统-任务看板编辑」或旧的「指挥系统-队员指令」；两点都还没入库时，能进指挥页即放行。
 */
export function canEditMissionBoard() {
  if (isAdminUser()) return true;
  if (!hasPermission('指挥系统页面')) return false;
  const permissionConfig = getPermissionConfig();
  const names = [COMMAND_FEATURE_PERMS.MISSION_BOARD_EDIT, COMMAND_FEATURE_PERMS.MEMBER_COMMAND];
  const existsInDb = Array.isArray(permissionConfig)
    && permissionConfig.some((p) => names.includes(p?.name));
  if (!existsInDb) return true;
  return names.some((name) => hasPermission(name));
}

// 检查用户是否有指定权限（仅 JWT role=admin 的系统管理员视为拥有全部）
export const hasPermission = (permissionName) => {
  if (isAdminUser()) return true;
  const permissions = getUserPermissions();
  return permissions.includes(permissionName);
};

// 检查权限点是否需要限制访问（兼容布尔 / 字符串 / 数字）
export const isRestrictedAccess = (permissionName) => {
  if (ALWAYS_RESTRICTED_PERMISSIONS.has(permissionName)) return true;
  const permissionConfig = getPermissionConfig();
  const permission = permissionConfig.find(p => p.name === permissionName);
  if (!permission) return false;
  const flag = permission.restrict_access;
  return flag === true || flag === 1 || flag === '1' || flag === 'true' || flag === 't';
};

/** 指挥系统对未登录用户一律不可见、不可进 */
function isCommandSystemPermission(permissionName) {
  const name = String(permissionName || '');
  return name === '指挥系统页面' || name.startsWith('指挥系统-');
}

// 检查页面是否应该显示给用户
export const shouldShowPage = (permissionName) => {
  // 访客：指挥系统隐藏；其它页看 guestAccess 或访客组权限
  if (isGuestUser()) {
    if (isCommandSystemPermission(permissionName)) return false;
    if (PERMISSION_CONFIG[permissionName]?.guestAccess) return true;
    return hasPermission(permissionName);
  }

  // 登录用户：限制访问的权限点才校验；公开权限点所有登录用户都能看到
  if (isRestrictedAccess(permissionName)) {
    return hasPermission(permissionName);
  }
  return true;
};

// 检查用户是否可以访问页面内容（与 shouldShowPage 同一套规则）
export const canAccessPage = (permissionName) => {
  if (isGuestUser()) {
    if (isCommandSystemPermission(permissionName)) return false;
    if (PERMISSION_CONFIG[permissionName]?.guestAccess) return true;
    return hasPermission(permissionName);
  }
  // 登录用户：限制访问的权限点才校验；公开权限点所有登录用户可用
  if (isRestrictedAccess(permissionName)) {
    return hasPermission(permissionName);
  }
  return true;
};

// 获取页面访问状态
export const getPageAccessStatus = (permissionName) => {
  const canShow = shouldShowPage(permissionName);
  const canAccess = canAccessPage(permissionName);
  
  if (!canShow) {
    return { status: 'hidden', message: '页面不可见' };
  } else if (!canAccess) {
    return { status: 'noAccess', message: '无权使用此功能' };
  } else {
    return { status: 'accessible', message: '可以正常使用' };
  }
};

// 权限点配置映射
export const PERMISSION_CONFIG = {
  '指挥系统页面': { name: '指挥系统页面', path: '/command', restrict_access: false, guestAccess: false },
  '指挥系统-获取轨迹': { name: '指挥系统-获取轨迹', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-查看轨迹': { name: '指挥系统-查看轨迹', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-下载轨迹': { name: '指挥系统-下载轨迹', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-导出轨迹': { name: '指挥系统-导出轨迹', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-轨迹回放': { name: '指挥系统-轨迹回放', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-实时绘制': { name: '指挥系统-实时绘制', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-地图画板': { name: '指挥系统-地图画板', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-画板推送': { name: '指挥系统-画板推送', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-底图切换': { name: '指挥系统-底图切换', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-队员指令': { name: '指挥系统-队员指令', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-任务看板编辑': { name: '指挥系统-任务看板编辑', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-查看全部队员': { name: '指挥系统-查看全部队员', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-查看授权组织队员': { name: '指挥系统-查看授权组织队员', path: '/command', restrict_access: true, guestAccess: false },
  '指挥系统-队员备注': { name: '指挥系统-队员备注', path: '/command', restrict_access: true, guestAccess: false },
  '设备管理': { name: '设备管理', path: '/devices', restrict_access: false, guestAccess: false },
  '坐标转换': { name: '坐标转换', path: '/coordinate', restrict_access: false, guestAccess: true },
  '轨迹查询': { name: '轨迹查询', path: '/history', restrict_access: false, guestAccess: false },
  '工具下载': { name: '工具下载', path: '/tools', restrict_access: false, guestAccess: true },
  '用户中心': { name: '用户中心', path: '/user', restrict_access: false, guestAccess: false },
  '救援推送': { name: '救援推送', path: '/rescue', restrict_access: true, guestAccess: false },
  '救援推送历史查看': { name: '救援推送历史查看', path: '/rescue', restrict_access: false, guestAccess: false },
  '救援推送历史删除': { name: '救援推送历史删除', path: '/rescue', restrict_access: false, guestAccess: false },
  // 有此权限可查看全部用户的坐标转换历史；无则仅本人（管理后台分组授权）
  '坐标转换历史记录查看': { name: '坐标转换历史记录查看', path: '/coordinate', restrict_access: true, guestAccess: false },
  '多窗口直播监控': { name: '多窗口直播监控', path: '/live', restrict_access: true, guestAccess: false },
  '航拍建模': { name: '航拍建模', path: '/command', restrict_access: true, guestAccess: false },
  '航拍建模审批': { name: '航拍建模审批', path: '/command', restrict_access: true, guestAccess: false },
  '航拍图层查看': { name: '航拍图层查看', path: '/command', restrict_access: true, guestAccess: false },
  '地图图层管理': { name: '地图图层管理', path: '/command', restrict_access: true, guestAccess: false },
  '地图图层查看': { name: '地图图层查看', path: '/command', restrict_access: true, guestAccess: false },
  '手机选点删除': { name: '手机选点删除', path: '/command', restrict_access: true, guestAccess: false },
  '用户管理': { name: '用户管理', path: '/admin', restrict_access: true, guestAccess: false },
  '分组管理': { name: '分组管理', path: '/admin', restrict_access: true, guestAccess: false },
  '权限管理': { name: '权限管理', path: '/admin', restrict_access: true, guestAccess: false },
  '用户授权': { name: '用户授权', path: '/admin', restrict_access: true, guestAccess: false },
  '系统设置': { name: '系统设置', path: '/admin', restrict_access: true, guestAccess: false },
  '管理后台访问': { name: '管理后台访问', path: '/admin', restrict_access: true, guestAccess: false },
  '分组显示': { name: '分组显示', path: '/admin', restrict_access: true, guestAccess: false },
  '工具管理': { name: '工具管理', path: '/admin', restrict_access: true, guestAccess: false },
  '会员信息管理': { name: '会员信息管理', path: '/admin', restrict_access: true, guestAccess: false },
  '公告管理': { name: '公告管理', path: '/admin', restrict_access: true, guestAccess: false },
  '坐标转换历史管理': { name: '坐标转换历史管理', path: '/admin', restrict_access: true, guestAccess: false },
  '成品管理': { name: '叠加图层', path: '/admin', restrict_access: true, guestAccess: false },
  '首页设置': { name: '首页设置', path: '/admin', restrict_access: true, guestAccess: false },
  '标题栏设置': { name: '标题栏设置', path: '/admin', restrict_access: true, guestAccess: false },
  '导航栏设置': { name: '导航栏设置', path: '/admin', restrict_access: true, guestAccess: false },
  '指挥系统设置': { name: '指挥系统设置', path: '/admin', restrict_access: true, guestAccess: false },
  '指挥系统密码': { name: '指挥系统密码', path: '/admin', restrict_access: true, guestAccess: false },
  '底图源设置': { name: '底图源设置', path: '/admin', restrict_access: true, guestAccess: false },
  '单兵 App 设置': { name: '单兵 App 设置', path: '/admin', restrict_access: true, guestAccess: false },
  '前线队员设置': { name: '前线队员设置', path: '/admin', restrict_access: true, guestAccess: false },
  '轨迹数据管理': { name: '轨迹数据管理', path: '/admin', restrict_access: true, guestAccess: false },
  '地图设置': { name: '地图设置', path: '/admin', restrict_access: true, guestAccess: false },
  '全景影像管理': { name: '全景影像管理', path: '/admin', restrict_access: true, guestAccess: false },
  '全景影像查看': { name: '全景影像查看', path: '/dashboard', restrict_access: true, guestAccess: false },
  '数据库结构': { name: '数据库结构', path: '/admin', restrict_access: true, guestAccess: false },
  '图标库管理': { name: '图标库管理', path: '/admin', restrict_access: true, guestAccess: false },
  '管理员密码管理': { name: '管理员密码管理', path: '/admin', restrict_access: true, guestAccess: false }
};

// 获取所有应该显示的导航链接
export const getVisibleNavLinks = () => {
  const visibleLinks = [];
  
  Object.entries(PERMISSION_CONFIG).forEach(([permName, config]) => {
    if (shouldShowPage(permName)) {
      visibleLinks.push({
        name: permName,
        path: config.path,
        canAccess: canAccessPage(permName),
        guestAccess: config.guestAccess || false
      });
    }
  });
  
  return visibleLinks;
};

// 无权限提示组件
export const NoAccessMessage = ({ permissionName }) => {
  const config = PERMISSION_CONFIG[permissionName];
  const displayName = config ? config.name : permissionName;
  const isGuest = isGuestUser();
  
  return (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px 20px',
      color: '#666'
    }}>
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
      <h2 style={{ color: '#333', marginBottom: '10px' }}>无权使用</h2>
      <p style={{ fontSize: '16px', marginBottom: '20px' }}>
        {isGuest ? '此功能需要登录后才能使用' : `您没有使用"${displayName}"功能的权限`}
      </p>
      <p style={{ fontSize: '14px', color: '#999', marginBottom: '20px' }}>
        {isGuest ? '请先登录或注册账号' : '请联系管理员为您分配相应权限'}
      </p>
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
        <button 
          onClick={() => window.history.back()} 
          style={{
            background: '#666',
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          返回上一页
        </button>
        {isGuest && (
          <button 
            onClick={() => window.location.href = '/login'} 
            style={{
              background: '#1976d2',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            去登录
          </button>
        )}
      </div>
    </div>
  );
};

// 暴露权限控制函数到全局，方便测试和调试
if (typeof window !== 'undefined') {
  window.permissionControl = {
    isGuestUser,
    isAdminUser,
    getCurrentUserId,
    getUserPermissions,
    getGuestPermissions,
    setGuestPermissions,
    getPermissionConfig,
    hasPermission,
    isRestrictedAccess,
    shouldShowPage,
    canAccessPage,
    getPageAccessStatus,
    PERMISSION_CONFIG,
    getVisibleNavLinks,
    NoAccessMessage
  };
} 